import React from 'react';
import { Box, Button, Container, Heading, Image, Text, HStack, VStack } from '@chakra-ui/react';
import { NavLink } from 'react-router-dom';
import { SectionHeader } from '@/components/SectionHeader';

const HeroSection: React.FC = () => {
  return (
    <Box
      position="relative"
      bg="gray.700"
      _dark={{
        bg: 'gray.800',
      }}
      py={{ base: '20', md: '48' }}
      overflow="hidden"
    >
      {/* Subtle background image layer */}
      <Image
        src="/home-hero.webp"
        alt="Modern apartment building"
        position="absolute"
        inset="0"
        width="100%"
        height="100%"
        objectFit="cover"
        objectPosition="center"
        opacity="0.4"
        pointerEvents="none"
        loading="eager"
        fetchPriority="high"
        _dark={{ opacity: '0.4' }}
      />

      {/* Dark gradient overlay for text readability while maintaining image visibility */}
      <Box
        position="absolute"
        inset="0"
        bgGradient="to-b"
        gradientFrom="blackAlpha.300"
        gradientVia="blackAlpha.200"
        gradientTo="blackAlpha.300"
        pointerEvents="none"
      />

      {/* Content layer */}
      <Container maxW="breakpoint-xl" position="relative" zIndex="1">
        <VStack gap="6" textAlign="center" maxW="4xl" mx="auto">
          <Heading
            as="h1"
            fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }}
            lineHeight="1.1"
            letterSpacing="tight"
            color="white"
          >
            Välkommen till Palatium Fastigheter
          </Heading>

          <Text
            fontSize={{ base: 'lg', md: 'xl' }}
            color="whiteAlpha.900"
            lineHeight="relaxed"
            maxW="2xl"
          >
            Vi äger och förvaltar bostadsfastigheter i Västerås, Arboga & Frövi med fokus på
            långsiktighet, kvalitet och trivsam boendemiljö.
          </Text>

          <HStack gap={{ base: '3', sm: '4' }} mt="4">
            <NavLink to="/available-properties">
              <Button size="lg" px={{ base: '4', md: '8' }}>
                Lediga bostäder
              </Button>
            </NavLink>
            <NavLink to="/rental-application">
              <Button size="lg" variant="subtle" px="8">
                Intresseanmälan
              </Button>
            </NavLink>
          </HStack>
        </VStack>
      </Container>
    </Box>
  );
};

const CTASection: React.FC = () => {
  return (
    <Box py={{ base: '20', md: '32' }}>
      <Container maxW="breakpoint-xl">
        <VStack gap="8" textAlign="center" maxW="3xl" mx="auto">
          <SectionHeader
            eyebrow="Behöver du hjälp?"
            heading="Har du frågor?"
            description="Kolla in vår FAQ-sida för svar på vanliga frågor. Hittar du inte det du letar efter kontaktar du oss enkelt via mail."
            maxW="3xl"
          />
          <HStack gap="4" mt="4" flexWrap="wrap" justifyContent="center">
            <NavLink to="/faq">
              <Button size="lg" px="8">
                Se vanliga frågor
              </Button>
            </NavLink>
            <NavLink to="mailto:info@palatium.se">
              <Button variant="subtle" size="lg" px="8">
                Kontakta oss
              </Button>
            </NavLink>
          </HStack>
        </VStack>
      </Container>
    </Box>
  );
};

const Homepage: React.FC = () => {
  return (
    <Box>
      <HeroSection />
      <CTASection />
    </Box>
  );
};

export default Homepage;
